import { ReleaseInfo as ReleaseInfoData } from '@/generated/ReleaseInfo';
import { _isNil, _noop, _notNil, getJwtPayload, getSessionCorrelationId, getTeamIdFromStorage } from '@/littledash';
import { RouteService } from '@/support/RouteService';
import { DateUtils } from '@/utils/Date.utils';
import { revalidateDocument } from '@/utils/ReleaseUpdate.utils';
import { debounceTime, fromEvent, merge, type Subscription } from 'rxjs';
import DeviceEventBus from '@/utils/devices/DeviceEventBus';

export interface ReleaseNoUpdate {
  type: 'no-update';
}

export interface ReleaseUpdateAvailable {
  type: 'update-available';
  time: number;
  commit: string;
  version: string;
}

export type ReleaseCheckResponse = ReleaseNoUpdate | ReleaseUpdateAvailable;

export interface ReleaseInfo {
  releaseVersion: string;
  commitId: string;
  commitTime: string;
}

interface AppStatusProps {
  onUpdateAvailable: (release: Omit<ReleaseUpdateAvailable, 'type'>) => void;
}

export interface RoundTripTime {
  average_ms: number | null;
  median_ms: number | null;
  min_ms: number | null;
  max_ms: number | null;
  success: number;
  error: number;
  total: number;
}

export type AppStatusSection = 'client' | 'update' | 'api' | 'page' | 'browser' | 'login';

/** `loading` and `error` mark fields whose request has not settled or failed; absent means loaded. */
export type AppStatusFieldState = 'loading' | 'error';

export type AppStatusField = {
  section: AppStatusSection;
  key: string;
  label: string;
  state?: AppStatusFieldState;
} & (
  | { type: 'boolean'; value: boolean | null }
  | { type: 'text'; value: string | null }
  | { type: 'number'; value: number | null; unit?: 'ms' | 'gb' | 'px' }
  | { type: 'datetime'; value: string | null }
  | { type: 'round_trip_time'; value: RoundTripTime | null }
);

export type AppStatusValue = AppStatusField['value'];

export type AppStatusReport = { generated_at: string } & Partial<
  Record<AppStatusSection, Record<string, AppStatusValue>>
>;

const asNumber = (value: unknown): number | null => (typeof value === 'number' ? value : null);
const asText = (value: unknown): string | null => (typeof value === 'string' ? value : null);
type Settled<T> = { state: 'loading' } | { state: 'loaded'; value: T | null } | { state: 'error' };
const LOADING = { state: 'loading' } as const;
const fromSettled = <T, V>(
  settled: Settled<T>,
  toValue: (value: T) => V
): { value: V | null; state?: AppStatusFieldState } => {
  if (settled.state === 'loaded') {
    return { value: _isNil(settled.value) ? null : toValue(settled.value) };
  }
  return { value: null, state: settled.state };
};
const navigationTiming = (): PerformanceNavigationTiming | null =>
  (performance.getEntriesByType('navigation').at(0) as PerformanceNavigationTiming | undefined) ?? null;
/** Navigation timing marks are 0 until reached, so 0 means "not yet". */
const reachedMark = (value: number | undefined): number | null => (_notNil(value) && value > 0 ? value : null);

/**
 * Release checking, the `window.InVivo` console API and the app status report shown on `/debug`
 * and returned by `window.InVivo.status()`.
 */
export class AppStatus {
  static #defaultTimeout = 1000 * 60 * 15; // 15 mins
  static #timeoutAfterInitialise = 1000 * 30; // 30 seconds
  static #probeCount = 10;
  #timeoutID: ReturnType<typeof setTimeout> | null = null;
  #eventSub$?: Subscription;
  #revalidatedForCommit: string | null = null;
  #destroyed = false;

  static async fetchLatestReleaseInfo(signal?: AbortSignal): Promise<ReleaseInfo> {
    const response = await fetch(`/release-info.json?v=${Date.now()}`, { signal });
    if (!response.ok) {
      throw new Error('Could not fetch latest release info', { cause: response.statusText });
    }
    return JSON.parse(await response.text()) as ReleaseInfo;
  }

  /**
   * Snapshot of release, API round-trip time, browser and login state. `onProgress` receives the
   * full field list straight away, with request-backed fields `loading`, and again as each request
   * settles; the promise resolves with the final list.
   */
  static async collectFields(
    signal: AbortSignal = new AbortController().signal,
    onProgress: (fields: Array<AppStatusField>) => void = _noop
  ): Promise<Array<AppStatusField>> {
    const online = navigator.onLine;
    const settle = async <T>(request: () => Promise<T>): Promise<Settled<T>> => {
      if (!online) {
        return { state: 'loaded', value: null };
      }
      try {
        return { state: 'loaded', value: await request() };
      } catch {
        return { state: 'error' };
      }
    };
    let release: Settled<ReleaseInfo> = LOADING;
    let rtt: Settled<RoundTripTime> = LOADING;
    let legacyRtt: Settled<RoundTripTime> = LOADING;
    const build = () => AppStatus.#buildFields({ release, rtt, legacyRtt, online });
    onProgress(build());
    await Promise.all([
      settle(() => AppStatus.fetchLatestReleaseInfo(signal)).then((result) => {
        release = result;
        onProgress(build());
      }),
      settle(() =>
        AppStatus.#measureRoundTripTime(
          RouteService.api({ endpoint: 'POST /api/v1/auth/login', path: undefined }).url,
          signal
        )
      ).then((result) => {
        rtt = result;
        onProgress(build());
      }),
      settle(() =>
        AppStatus.#measureRoundTripTime(RouteService.legacyApi({ apiRoute: 'global-data' }).url, signal)
      ).then((result) => {
        legacyRtt = result;
        onProgress(build());
      }),
    ]);
    return build();
  }

  static #buildFields({
    release,
    rtt,
    legacyRtt,
    online,
  }: {
    release: Settled<ReleaseInfo>;
    rtt: Settled<RoundTripTime>;
    legacyRtt: Settled<RoundTripTime>;
    online: boolean;
  }): Array<AppStatusField> {
    const latestRelease = release.state === 'loaded' ? release.value : null;
    const updatePending = _notNil(latestRelease) && latestRelease.commitId !== ReleaseInfoData.commitId;
    const nextUpdateCheck = AppStatus.#readNextCheckTime();
    const jwtPayload = getJwtPayload();
    const navigation = navigationTiming();
    const fields: Array<AppStatusField> = [
      {
        section: 'client',
        key: 'up_to_date',
        label: 'Client - Up To Date',
        type: 'boolean',
        ...fromSettled(release, (latest) => latest.commitId === ReleaseInfoData.commitId),
      },
    ];
    if (updatePending) {
      fields.push(
        {
          section: 'update',
          key: 'release_version',
          label: 'Update - Release Version',
          type: 'text',
          value: latestRelease.releaseVersion,
        },
        {
          section: 'update',
          key: 'release_id',
          label: 'Update - Release ID',
          type: 'text',
          value: latestRelease.commitId,
        },
        {
          section: 'update',
          key: 'release_time',
          label: 'Update - Release Time',
          type: 'datetime',
          value: latestRelease.commitTime,
        }
      );
    }
    fields.push(
      {
        section: 'client',
        key: 'release_version',
        label: 'Client - Release Version',
        type: 'text',
        value: ReleaseInfoData.releaseVersion,
      },
      {
        section: 'client',
        key: 'release_id',
        label: 'Client - Release ID',
        type: 'text',
        value: ReleaseInfoData.commitId,
      },
      {
        section: 'client',
        key: 'release_time',
        label: 'Client - Release Time',
        type: 'datetime',
        value: ReleaseInfoData.commitTime,
      },
      {
        section: 'client',
        key: 'next_update_check',
        label: 'Client - Next Update Check',
        type: 'datetime',
        value: nextUpdateCheck > 0 ? new Date(nextUpdateCheck).toISOString() : null,
      },
      {
        section: 'api',
        key: 'rtt',
        label: 'API - Round Trip Time',
        type: 'round_trip_time',
        ...fromSettled(rtt, (value) => value),
      },
      {
        section: 'api',
        key: 'legacy_rtt',
        label: 'API - Round Trip Time (legacy)',
        type: 'round_trip_time',
        ...fromSettled(legacyRtt, (value) => value),
      },
      {
        section: 'page',
        key: 'ttfb_ms',
        label: 'Page - Time To First Byte',
        type: 'number',
        unit: 'ms',
        value: reachedMark(navigation?.responseStart),
      },
      {
        section: 'page',
        key: 'dom_content_loaded_ms',
        label: 'Page - DOM Content Loaded',
        type: 'number',
        unit: 'ms',
        value: reachedMark(navigation?.domContentLoadedEventEnd),
      },
      {
        section: 'page',
        key: 'load_ms',
        label: 'Page - Load',
        type: 'number',
        unit: 'ms',
        value: reachedMark(navigation?.loadEventEnd),
      },
      { section: 'browser', key: 'online', label: 'Browser - Online', type: 'boolean', value: online },
      {
        section: 'browser',
        key: 'platform',
        label: 'Browser - Platform',
        type: 'text',
        value: asText(navigator.userAgentData?.platform),
      },
      {
        section: 'browser',
        key: 'user_agent',
        label: 'Browser - User Agent',
        type: 'text',
        value: navigator.userAgent,
      },
      { section: 'browser', key: 'language', label: 'Browser - Language', type: 'text', value: navigator.language },
      { section: 'browser', key: 'timezone', label: 'Browser - Timezone', type: 'text', value: DateUtils.timezone() },
      {
        section: 'browser',
        key: 'connection_type',
        label: 'Browser - Connection Type',
        type: 'text',
        value: asText(navigator.connection?.type),
      },
      {
        section: 'browser',
        key: 'connection_effective_type',
        label: 'Browser - Connection Effective Type',
        type: 'text',
        value: asText(navigator.connection?.effectiveType),
      },
      {
        section: 'browser',
        key: 'connection_rtt_ms',
        label: 'Browser - Connection Round Trip Time',
        type: 'number',
        unit: 'ms',
        value: asNumber(navigator.connection?.rtt),
      },
      {
        section: 'browser',
        key: 'device_memory_gb',
        label: 'Browser - Device Memory',
        type: 'number',
        unit: 'gb',
        value: asNumber(navigator.deviceMemory),
      },
      {
        section: 'browser',
        key: 'hardware_concurrency',
        label: 'Browser - Hardware Concurrency',
        type: 'number',
        value: asNumber(navigator.hardwareConcurrency),
      },
      {
        section: 'browser',
        key: 'max_touch_points',
        label: 'Browser - Max Touch Points',
        type: 'number',
        value: asNumber(navigator.maxTouchPoints),
      },
      {
        section: 'browser',
        key: 'viewport_width',
        label: 'Browser - Viewport Width',
        type: 'number',
        unit: 'px',
        value: window.innerWidth,
      },
      {
        section: 'browser',
        key: 'viewport_height',
        label: 'Browser - Viewport Height',
        type: 'number',
        unit: 'px',
        value: window.innerHeight,
      },
      {
        section: 'browser',
        key: 'screen_width',
        label: 'Browser - Screen Width',
        type: 'number',
        unit: 'px',
        value: window.screen.width,
      },
      {
        section: 'browser',
        key: 'screen_height',
        label: 'Browser - Screen Height',
        type: 'number',
        unit: 'px',
        value: window.screen.height,
      },
      {
        section: 'browser',
        key: 'pdf_viewer_enabled',
        label: 'Browser - PDF Viewer Enabled',
        type: 'boolean',
        value: navigator.pdfViewerEnabled ?? null,
      },
      {
        section: 'login',
        key: 'session_id',
        label: 'Login - Session ID',
        type: 'text',
        value: getSessionCorrelationId(),
      },
      {
        section: 'login',
        key: 'valid',
        label: 'Login - Valid',
        type: 'boolean',
        value: _isNil(jwtPayload) ? null : jwtPayload.exp * 1000 > Date.now(),
      },
      {
        section: 'login',
        key: 'issued_at',
        label: 'Login - Issued At',
        type: 'datetime',
        value: _isNil(jwtPayload) ? null : new Date(jwtPayload.iat * 1000).toISOString(),
      },
      {
        section: 'login',
        key: 'expires_at',
        label: 'Login - Expires At',
        type: 'datetime',
        value: _isNil(jwtPayload) ? null : new Date(jwtPayload.exp * 1000).toISOString(),
      },
      { section: 'login', key: 'user_id', label: 'Login - User ID', type: 'text', value: jwtPayload?.sub ?? null },
      { section: 'login', key: 'team_id', label: 'Login - Team ID', type: 'text', value: getTeamIdFromStorage() }
    );
    return fields;
  }

  /**
   * JSON-serialisable form of {@link AppStatus.collectFields}, keyed by section then field key;
   * `generated_at` is when collection started. Returned by `window.InVivo.status()` for Datadog
   * synthetics.
   */
  static async collect(signal?: AbortSignal): Promise<AppStatusReport> {
    const generatedAt = new Date().toISOString();
    const fields = await AppStatus.collectFields(signal);
    return fields.reduce<AppStatusReport>(
      (report, { section, key, value }) => {
        report[section] = { ...report[section], [key]: value };
        return report;
      },
      { generated_at: generatedAt }
    );
  }

  /**
   * Mean, median, min and max time to response headers over {@link AppStatus.#probeCount} sequential `OPTIONS` probes,
   * after one discarded warm-up probe that absorbs DNS/TCP/TLS setup and, cross-origin, the CORS
   * preflight that the browser then caches. `OPTIONS` is answered by the servers' CORS layer, so it
   * times the network rather than application auth and routing.
   */
  static async #measureRoundTripTime(url: URL, signal: AbortSignal): Promise<RoundTripTime> {
    await AppStatus.#probe(url, signal);
    const samples = await Array.from({ length: AppStatus.#probeCount }).reduce<Promise<Array<number | null>>>(
      async (previous) => [...(await previous), await AppStatus.#probe(url, signal)],
      Promise.resolve([])
    );
    const durations = samples
      .reduce<Array<number>>((acc, sample) => {
        if (_notNil(sample)) {
          acc.push(sample);
        }
        return acc;
      }, [])
      .sort((a, b) => a - b);
    const success = durations.length;
    const middle = Math.floor(success / 2);
    return {
      average_ms: success > 0 ? durations.reduce((sum, duration) => sum + duration, 0) / success : null,
      median_ms:
        success === 0 ? null : success % 2 === 1 ? durations[middle] : (durations[middle - 1] + durations[middle]) / 2,
      min_ms: durations.at(0) ?? null,
      max_ms: durations.at(-1) ?? null,
      success,
      error: samples.length - success,
      total: samples.length,
    };
  }

  static async #probe(url: URL, signal: AbortSignal): Promise<number | null> {
    const start = performance.now();
    try {
      await fetch(url, {
        method: 'OPTIONS',
        cache: 'no-store',
        signal,
      });
      return performance.now() - start;
    } catch {
      return null;
    }
  }

  static #readNextCheckTime(): number {
    const value = Number(localStorage.getItem('release-check.next') ?? '0');
    return Number.isFinite(value) ? value : 0;
  }

  private props: AppStatusProps;

  constructor(props: AppStatusProps) {
    this.props = props;
  }

  initialise(): AppStatus {
    window['InVivo'] = {
      status: () => AppStatus.collect(),
      enableFeatures: (...flags: Array<string>) =>
        this.updateFeatureFlags(
          flags.map((name) => ({
            name,
            value: true,
          }))
        ),
      disableFeatures: (...flags: Array<string>) =>
        this.updateFeatureFlags(
          flags.map((name) => ({
            name,
            value: false,
          }))
        ),
      device_event_bus: DeviceEventBus,
    };
    this.#eventSub$ = merge(
      fromEvent(document, 'visibilitychange', { passive: true }),
      fromEvent(window, 'online', { passive: true }),
      fromEvent(window, 'offline', { passive: true })
    )
      .pipe(debounceTime(1000))
      .subscribe(() => {
        if (navigator.onLine && document.visibilityState === 'visible') {
          this.#scheduleCheck({ timeout: this.#initialCheckTimeout, force: true });
        } else {
          this.#clearSchedule();
        }
      });
    this.#scheduleCheck({ timeout: this.#initialCheckTimeout });
    return this;
  }

  destroy() {
    this.#destroyed = true;
    this.#eventSub$?.unsubscribe();
    this.#clearSchedule();
    delete window['InVivo'];
  }

  get #nextCheckTime(): number {
    return AppStatus.#readNextCheckTime();
  }

  set #nextCheckTime(value: number) {
    localStorage.setItem('release-check.next', `${value}`);
  }

  /**
   * How long to wait before the first check of this page load, or of a tab the user has just
   * returned to. The persisted deadline throttles rapid reloads, but it must never postpone
   * these moments — they are exactly when a stale bundle surfaces — so it is capped. The
   * deadline is written before the check runs and is shared across tabs, so an inherited
   * value can otherwise be up to a full interval in the future.
   */
  get #initialCheckTimeout(): number {
    const remaining = this.#nextCheckTime - Date.now();
    if (remaining <= 0) {
      return AppStatus.#timeoutAfterInitialise;
    }
    return Math.min(remaining, AppStatus.#timeoutAfterInitialise);
  }

  #clearSchedule() {
    if (_notNil(this.#timeoutID)) {
      clearTimeout(this.#timeoutID);
      this.#timeoutID = null;
    }
  }

  /**
   * A check that is already awaiting the network has no timer to clear, so a `destroy()`
   * landing during that await cannot stop the re-arm that follows it — both
   * `checkForUpdateNow()` and the polling cadence re-arm from a `finally`. Without this guard
   * that re-armed timer is unreachable by any later cleanup and keeps rescheduling itself forever.
   */
  #scheduleCheck(props: { force?: boolean; timeout?: number; reschedule?: boolean } = {}) {
    if (this.#destroyed) {
      return;
    }
    if (navigator.onLine && document.visibilityState === 'visible') {
      const force = props?.force ?? false;
      const reschedule = props?.reschedule ?? true;
      const timeout = props?.timeout ?? AppStatus.#defaultTimeout;
      if (_isNil(this.#timeoutID) || force) {
        this.#clearSchedule();
        this.#nextCheckTime = Date.now() + timeout;
        this.#timeoutID = setTimeout(() => {
          this.#timeoutID = null;
          this.checkForUpdate()
            .then((result) => {
              if (result.type === 'update-available' && !this.#destroyed) {
                this.props.onUpdateAvailable({ commit: result.commit, version: result.version, time: result.time });
              }
            })
            .catch(_noop)
            .finally(() => {
              if (reschedule) {
                this.#scheduleCheck();
              }
            });
        }, timeout);
      }
    }
  }

  async checkForUpdate(): Promise<ReleaseCheckResponse> {
    try {
      const {
        releaseVersion: latestReleaseVersion,
        commitTime: latestReleaseCommitTime,
        commitId: latestReleaseCommitId,
      } = await AppStatus.fetchLatestReleaseInfo();
      if (_notNil(latestReleaseCommitId) && ReleaseInfoData.commitId !== latestReleaseCommitId) {
        const time = Date.parse(latestReleaseCommitTime);
        this.#revalidateForRelease(latestReleaseCommitId);
        return { type: 'update-available', commit: latestReleaseCommitId, version: latestReleaseVersion, time };
      }
      return { type: 'no-update' };
    } catch (e) {
      return Promise.reject(new Error('Release Check Failed', { cause: e }));
    }
  }

  /**
   * Pulls the new index.html into the HTTP cache the moment an update is detected, so any
   * later document load — the update toast, a manual refresh, a browser session restore, the
   * chunk-load recovery — boots the new release instead of replaying the document that went
   * stale. This cannot rescue the running page, whose bundle still asks for the old chunk
   * names. Once per detected release: the poll re-reports the same update every interval and
   * re-fetching each time would be pointless traffic.
   */
  #revalidateForRelease(commitId: string): void {
    if (this.#revalidatedForCommit === commitId) {
      return;
    }
    this.#revalidatedForCommit = commitId;
    revalidateDocument().catch(_noop);
  }

  /**
   * Runs a release check immediately, outside the polling schedule, and resolves with the
   * result so the caller decides how to react. Deliberately not gated on `navigator.onLine`
   * or `document.visibilityState` the way the polling schedule is: the only caller is a
   * just-completed authentication handshake, which proves connectivity far better than
   * `navigator.onLine` does, and a backgrounded tab redeeming an SSO short-lived token is
   * precisely the case that must not be skipped. The regular cadence is re-armed afterwards
   * so the schedule is never lost.
   */
  async checkForUpdateNow(): Promise<ReleaseCheckResponse> {
    this.#clearSchedule();
    try {
      return await this.checkForUpdate();
    } finally {
      this.#scheduleCheck();
    }
  }

  private async updateFeatureFlags(flags: Array<{ name: string; value: boolean }>) {
    const { method, url } = RouteService.api({
      endpoint: 'PATCH /api/v1/team/feature-flags',
      path: undefined,
    });
    const response = await fetch(url, {
      method,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
        'Team-Id': localStorage.getItem('team_id') ?? '',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(flags),
    });
    /* eslint-disable no-console */
    if (response.ok) {
      console.log(
        `%c Updated ${flags.length} flag(s) `,
        'font-style: bold; font-size: 2em; background-color: green; color: white; padding: 5px;'
      );
    } else {
      console.log(
        `%c Update failed for ${flags.length} flag(s) `,
        'background-color: red; color: yellow; font-style: bold; font-size: 2em; padding: 5px;'
      );
    }
    /* eslint-enable no-console */
  }
}
